// Subscription Status
export type SubscriptionStatus = 'trial' | 'active' | 'past_due' | 'canceled' | 'expired';

// Billing Interval
export type BillingInterval = 'monthly' | 'yearly';

// Subscription Features
export type SubscriptionFeature =
  | 'analytics'
  | 'management'
  | 'export_data'
  | 'advanced_reports'
  | 'priority_support'
  | 'custom_branding'
  | 'api_access'
  | 'unlimited_maps'
  | 'unlimited_referees';

// Subscription Plan
export interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  priceMonthly: number; // в центах
  priceYearly: number; // в центах
  currency: string;
  features: SubscriptionFeature[];
  trialDays: number;
}

// Subscription
export interface Subscription {
  id: string;
  hasSubscription: boolean;
  status: SubscriptionStatus;
  planId: string;
  planName: string;
  billingInterval: BillingInterval;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialStart?: string;
  trialEnd?: string;
  cancelAtPeriodEnd: boolean;
  activeFeatures: SubscriptionFeature[];
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
}

// Trial Info
export interface TrialInfo {
  isTrialing: boolean;
  trialStart?: string;
  trialEnd?: string;
  daysRemaining?: number;
  hasExpiredTrial?: boolean;
}

// Access Info (для FeatureGateService)
export interface AccessInfo {
  hasAccess: boolean;
  status: SubscriptionStatus | null;
  activeFeatures: SubscriptionFeature[];
  expiresAt: string | null;
  isTrialing: boolean;
  trialDaysRemaining: number | null;
}

// Payment History
export interface PaymentHistoryItem {
  id: string;
  amount: number; // в центах
  currency: string;
  status: 'succeeded' | 'failed' | 'pending' | 'refunded';
  paymentMethod: string;
  periodStart: string;
  periodEnd: string;
  description: string;
  createdAt: string;
}

// Checkout Session Response
export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

// Portal Session Response
export interface PortalSessionResponse {
  url: string;
}

// Requests
export interface CreateCheckoutSessionRequest {
  planId: string;
  billingInterval: BillingInterval;
}

export interface CreatePortalSessionRequest {
  returnUrl?: string;
}

export interface CancelSubscriptionRequest {
  atPeriodEnd?: boolean;
}

// Feature Check Response
export interface FeatureCheckResponse {
  hasFeature: boolean;
  feature: string;
}

// No Subscription Response
export interface NoSubscriptionResponse {
  hasSubscription: false;
}

// Subscription Response (union type)
export type SubscriptionResponse = Subscription | NoSubscriptionResponse;

// Helper function для проверки наличия подписки
export function hasSubscription(response: SubscriptionResponse): response is Subscription {
  return response.hasSubscription === true;
}

// Helper function для форматирования цены
export function formatPrice(cents: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(cents / 100);
}

// Helper function для расчёта оставшихся дней
export function getDaysRemaining(endDate: string): number {
  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}
